import { useCallback, useEffect, useState } from "react";
import { Banner, BlockStack, Button, Checkbox, DataTable, InlineStack, Text, TextField } from "@shopify/polaris";
import axios from "axios";
import agent from "../../app/api/agent";
import { AdminLegacyGarageOperation } from "../../app/models/Admin";
import { formatLocalDateTime } from "./utils/dateTime";
import MigrationDetails from './MigrationDetails';
import PilotFailureDetails from './PilotFailureDetails';

type Props = { sourceShopId: number; blockedReason?: string; token: string; onUnauthorized: () => void };

export default function LegacyGarageMigrationPanel({ sourceShopId, blockedReason, token, onUnauthorized }: Props) {
  const [operations, setOperations] = useState<AdminLegacyGarageOperation[]>([]);
  const [captureConfirmed, setCaptureConfirmed] = useState(false);
  const [timeout, setTimeoutValue] = useState("120");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setOperations(await agent.Admin.sourceGarageOperations(token, sourceShopId));
      setLoaded(true);
      setError(null);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError("Unable to load garage migration operations.");
    }
  }, [token, sourceShopId, onUnauthorized]);

  const active = operations.some(x => x.status === "Queued" || x.status === "Running");
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void load(), 10000);
    return () => window.clearInterval(timer);
  }, [active, load]);

  const preflight = operations.find(x => x.operationType === "Preflight");
  const canStage = !!preflight && (preflight.status === "Completed" || preflight.status === "CompletedWithIssues")
    && preflight.unresolvedAtStart === 0 && preflight.unresolvedAtEnd === 0;
  const timeoutMinutes = Number(timeout);
  const canQueue = !blockedReason && loaded && captureConfirmed && !busy && !active
    && Number.isInteger(timeoutMinutes) && timeoutMinutes >= 1 && timeoutMinutes <= 240;
  const latest = operations.find(x => x.id === selectedRunId) ?? operations[0];

  const queue = async (type: "Preflight" | "StageBaseline") => {
    if (!canQueue) return;
    if (type === "StageBaseline" && !window.confirm("Start a fresh historical baseline in the isolated staging tables? Active storefront garages will not be changed.")) return;
    setBusy(true);
    setError(null);
    try {
      const operation = await agent.Admin.queueSourceGarageOperation(token, sourceShopId, type, captureConfirmed, timeoutMinutes);
      setSelectedRunId(operation.id);
      setOperations(current => [operation, ...current].slice(0, 20));
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === "string"
        ? e.response.data.message : "Unable to queue garage migration operation.");
    } finally { setBusy(false); }
  };

  return <BlockStack gap="400">
    <Text as="h3" variant="headingMd">Garage tools & run history</Text>
    <Text as="p">Pilot activation prepares garages automatically. Use these tools for a separate preflight or historical backfill.</Text>
    {blockedReason ? <Banner tone="warning">{blockedReason}</Banner> : null}
    {error ? <Banner tone="critical">{error}</Banner> : null}
    <Checkbox label="I have verified PFV1 durable garage change capture is enabled and recording for this shop."
      checked={captureConfirmed} onChange={setCaptureConfirmed} disabled={active || busy} />
    <div style={{ maxWidth: 260 }}><TextField label="Maximum run time (minutes)" type="number" autoComplete="off" min={1} max={240}
      value={timeout} onChange={setTimeoutValue} disabled={active || busy} /></div>
    <InlineStack gap="200">
      <Button onClick={() => void queue("Preflight")} disabled={!canQueue} loading={busy}>Run preflight</Button>
      <Button onClick={() => void queue("StageBaseline")} disabled={!canQueue || !canStage}>Backfill staging tables</Button>
      <Button onClick={() => void load()}>Refresh status</Button>
    </InlineStack>
    <Text as="p" tone="subdued">Runs continue in ShopifySyncService after you leave this page. Status refreshes every 10 seconds while active. Backfill requires a completed preflight with no unresolved capture events. Review invalid/tag-review records before any later promotion.</Text>
    {latest ? <>
      <Text as="h4" variant="headingSm">Run report: {latest.operationType} — {latest.status}</Text>
      {latest.status === "Queued" ? <Banner tone="info">Waiting for the garage migration worker. If this persists, verify ShopifySyncService is running with LegacyGarageMigration:Enabled set to true.</Banner> : null}
      {latest.failureCode ? <Banner tone="warning" title="Run stopped"><PilotFailureDetails operation={latest} /></Banner> : null}
      <MigrationDetails title="Scan counts & worker details">
      <DataTable columnContentTypes={["text", "numeric"]} headings={["Aggregate", "Count"]} rows={[
        ["PFV1 SQL saved-model rows scanned", latest.sqlRowsScanned],
        ["Shopify customers scanned", latest.customersScanned],
        ["Shopify customers with valid vehicle entries", latest.customersWithGarageData],
        ["Source records found (SQL + Shopify; not unique customers)", latest.garageRecords],
        ["Valid vehicle entries (not deduplicated across sources)", latest.validVehicleEntries],
        ["Records requiring review", latest.invalidRecords],
        ["Duplicate entries within source records", latest.duplicateVehicleEntries],
        ["Unresolved capture events at start", latest.unresolvedAtStart],
        ["Unresolved capture events at end", latest.unresolvedAtEnd ?? "—"],
      ]} />
      <Text as="p" tone="subdued">Last worker heartbeat: {formatLocalDateTime(latest.heartbeatAt)}. Bulk operation: {latest.bulkOperationId || "not started"}. Staging run: {latest.stagingRunId || "none (preflight only)"}.</Text>
      </MigrationDetails>
    </> : <Text as="p">No garage migration runs yet.</Text>}
    <DataTable columnContentTypes={["text", "text", "text", "text", "text"]}
      headings={["Run", "Status", "Requested", "Completed", "Requested by"]}
      rows={operations.map(x => [<Button variant="plain" onClick={() => setSelectedRunId(x.id)}>{x.operationType} ({x.id.slice(0, 8)})</Button>, x.status,
        formatLocalDateTime(x.requestedAt), formatLocalDateTime(x.completedAt), x.requestedBy])} />
  </BlockStack>;
}
